
import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    TextField,
    Button,
    Typography,
    MenuItem,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    IconButton
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import apiUrl from '../../Config';
import HtmlEditor from '../../Common/HtmlEditor';
import CloseIcon from '@mui/icons-material/Close';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AddEditBooks = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const editingBook = state?.book;

    // All state declarations
    const [form, setForm] = useState({
        subCategoryId: '',
        title: '',
        name: '',
        description: '',
        edition: '',
        authorId: '',
        publication: '',
        availability: true,
        code: '',
        language: '',
        format: '',
        ISBN: '',
        publishingYear: '',
        price: '',
        discountOffer: '',
        aboutTheBook: '',
        isNewRelease: false,
        isUpcoming: false,
        isTodayOffers: false,
        isPopular: false,
        isActive: true,
        previewVideoUrl: "",
    });

    const [bookImages, setBookImages] = useState([]);

    const [bookImagePreviews, setBookImagePreviews] = useState([]);
    const [subCategory, setSubCategory] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [authors, setAuthors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    const [privewPdf, setprivewPdf] = useState(null);
    const [previewPdfUrl, setPreviewPdfUrl] = React.useState(null);




    useEffect(() => {
        const fetchInitials = async () => {
            setLoading(true);
            try {
                const [subRes, langRes, authRes] = await Promise.all([
                    axios.get(`${apiUrl}/subcategories/getallsubcategories`),
                    axios.get(`${apiUrl}/languages/getalllanguage`),
                    axios.get(`${apiUrl}/authors/getauthors`),
                ]);
                setSubCategory(subRes.data.subCategories);
                setLanguages(langRes.data.languages || []);
                setAuthors(authRes.data.authors);
            } catch (error) {
                toast.error('Failed to load initial data');
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchInitials();
    }, []);

    useEffect(() => {
        if (editingBook) {
            setForm({
                ...editingBook,
                authorId: editingBook?.author?.id || '',
                language: editingBook?.language || ''
            });
            const existingImages = (editingBook.bookImages || []).map(img => ({
                type: 'existing',
                id: img.id,
                preview: img.imageUrl
            }));
            setBookImages(existingImages);
            setBookImagePreviews(editingBook?.bookImages?.map(img => img.imageUrl) || []);
            setPreviewPdfUrl(editingBook?.previewUrl || '')
        }
    }, [editingBook]);

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value
        });
        // Clear error when user starts typing
        if (formErrors[e.target.name]) {
            setFormErrors({
                ...formErrors,
                [e.target.name]: null
            });
        }
    };

    const handleCheckboxChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.checked
        });
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const totalAllowed = 4 - bookImages.length;
        const filesToAdd = selectedFiles.slice(0, totalAllowed).map(file => ({
            type: 'new',
            data: file,
            preview: URL.createObjectURL(file)
        }));
        setBookImages(prev => [...prev, ...filesToAdd]);
    };


   const handleRemoveBookImage = async (idx) => {
    const imageToRemove = bookImages[idx];

    // Remove from UI
    const updatedImages = bookImages.filter((_, i) => i !== idx);
    setBookImages(updatedImages);

    // Cleanup object URL if needed
    if (imageToRemove.type === 'new') {
        URL.revokeObjectURL(imageToRemove.preview);
    }

    // Call delete API for existing image
    if (imageToRemove.type === 'existing') {
        console.log(imageToRemove,"remiveimage")
        try {
           const response= await axios.delete(apiUrl+`/books/deletebookimage/${imageToRemove.id}`);
           if(response?.data?.status===true){
            toast.success(response?.data?.message)
           } // <-- your API call
        } catch (err) {
           toast.error('Failed to delete image:', err);
           
        }
    }
};


    const validateForm = () => {
        const errors = {};
        let isValid = true;

        // Required fields validation
        if (!form.name.trim()) {
            errors.name = 'Product Name is required';
            isValid = false;
        }
        if (!form.title.trim()) {
            errors.title = 'Title is required';
            isValid = false;
        }
        if (!form.subCategoryId) {
            errors.subCategoryId = 'Sub Category is required';
            isValid = false;
        }
        if (!form.authorId) {
            errors.authorId = 'Author is required';
            isValid = false;
        }
        if (!form.publication.trim()) {
            errors.publication = 'Publication is required';
            isValid = false;
        }
        if (!form.price) {
            errors.price = 'Price is required';
            isValid = false;
        } else if (isNaN(form.price) || form.price <= 0) {
            errors.price = 'Price must be a positive number';
            isValid = false;
        }
        if (form.discountOffer && (isNaN(form.discountOffer) || form.discountOffer < 0 || form.discountOffer > 100)) {
            errors.discountOffer = 'Discount must be between 0 and 100';
            isValid = false;
        }
        if (!form.ISBN.trim()) {
            errors.ISBN = 'ISBN is required';
            isValid = false;
        }
        if (!form.edition.trim()) {
            errors.edition = 'Edition is required';
            isValid = false;
        }
        if (!form.code.trim()) {
            errors.code = 'Code is required';
            isValid = false;
        }
        if (!form.language.trim()) {
            errors.language = 'Language is required';
            isValid = false;
        }
        if (!form.format.trim()) {
            errors.format = 'Format is required';
            isValid = false;
        }
        if (!form.publishingYear.trim()) {
            errors.publishingYear = 'Publishing Year is required';
            isValid = false;
        }
        if (!form.aboutTheBook.trim()) {
            errors.aboutTheBook = 'About the Book is required';
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };

    const handlePdfUpload = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setprivewPdf(file);
            setPreviewPdfUrl(URL.createObjectURL(file));
        } else {
            toast.error("Please upload a valid PDF file.");
        }
    };



    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fix all validation errors');
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            Object.entries(form).forEach(([key, val]) => {
                if (val !== null && val !== undefined) {
                    formData.append(key, val);
                }
            });

            bookImages.forEach(item => {
                if (item.type === 'new') {
                    formData.append('bookImage', item.data);
                }
            });

            if (privewPdf) {
                formData.append('previewPdf', privewPdf);
            }

            const url = editingBook ?
                `${apiUrl}/books/updatebook/${editingBook.id}` :
                `${apiUrl}/books/createbook`;

            await axios[editingBook ? 'put' : 'post'](url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            toast.success(editingBook ? 'Book updated successfully!' : 'Book created successfully!');
            setTimeout(() => navigate('/display-books'), 1500);
        } catch (err) {
            console.error('Submission error:', err);
            toast.error(err.response?.data?.message || 'Error saving book. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3, color: '#000', minHeight: '100vh' }}>
            <ToastContainer position="top-right" autoClose={5000} />
            <Typography variant="h3" color="initial" my={1.5}>
                {editingBook ? 'Edit Book' : 'Create New Book'}
            </Typography>

            <Box component="form" onSubmit={handleSubmit} mt={3}>
                <Grid container spacing={3}>
                    <Grid item size={{ xs: 12 }}>
                        {/* General Information */}
                        <Box sx={{ bgcolor: '#fff', p: 3, borderRadius: 2, mb: 3, border: '1px solid lightgrey' }}>
                            <Typography variant="h6" gutterBottom>General Information</Typography>
                            <Grid container spacing={2}>
                                <Grid item size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Product Name *"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Title *"
                                        name="title"
                                        value={form.title}
                                        onChange={handleChange}
                                        error={!!formErrors.title}
                                        helperText={formErrors.title}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Sub Category *"
                                        name="subCategoryId"
                                        value={form.subCategoryId}
                                        onChange={handleChange}
                                        error={!!formErrors.subCategoryId}
                                        helperText={formErrors.subCategoryId}
                                    >
                                        {subCategory?.map(cat => (
                                            <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Author *"
                                        name="authorId"
                                        value={form.authorId}
                                        onChange={handleChange}
                                        error={!!formErrors.authorId}
                                        helperText={formErrors.authorId}
                                    >
                                        {authors?.map(author => (
                                            <MenuItem key={author.id} value={author.id}>{author.name}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Publication *"
                                        name="publication"
                                        value={form.publication}
                                        onChange={handleChange}
                                        error={!!formErrors.publication}
                                        helperText={formErrors.publication}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        size="small"
                                        label="Description"
                                        name="description"
                                        value={form.description}
                                        error={!!formErrors.description}
                                        onChange={handleChange}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Pricing & Availability */}
                        <Box sx={{ bgcolor: '#fff', p: 3, borderRadius: 2, mb: 3, border: '1px solid lightgrey' }}>
                            <Typography variant="h6" gutterBottom>Pricing & Availability</Typography>
                            <Grid container spacing={2}>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Price (₹) *"
                                        name="price"
                                        value={form.price}
                                        onChange={handleChange}
                                        error={!!formErrors.price}
                                        helperText={formErrors.price}
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Discount (%)"
                                        name="discountOffer"
                                        value={form.discountOffer}
                                        error={!!formErrors.discountOffer}
                                        onChange={handleChange}
                                        type="number"
                                        inputProps={{ min: 0, max: 100 }}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={form.availability}
                                                onChange={(e) => setForm({ ...form, availability: e.target.checked })}
                                                name="availability"
                                            />
                                        }
                                        label="Available"
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={form.isActive}
                                                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                                                name="isActive"
                                            />
                                        }
                                        label="Is Active"
                                    />
                                </Grid>
                            </Grid>
                            <Typography sx={{ mt: 2, mb: 1 }}>Book Flags</Typography>
                            <Grid container spacing={1}>
                                <Grid item size={{ xs: 'auto' }}>
                                    <FormControlLabel
                                        control={<Checkbox checked={form.isNewRelease} onChange={handleCheckboxChange} name="isNewRelease" />}
                                        label="New Release"
                                    />
                                </Grid>
                                <Grid item size={{ xs: 'auto' }}>
                                    <FormControlLabel
                                        control={<Checkbox checked={form.isUpcoming} onChange={handleCheckboxChange} name="isUpcoming" />}
                                        label="Upcoming"
                                    />
                                </Grid>
                                <Grid item size={{ xs: 'auto' }}>
                                    <FormControlLabel
                                        control={<Checkbox checked={form.isTodayOffers} onChange={handleCheckboxChange} name="isTodayOffers" />}
                                        label="Today's Offer"
                                    />
                                </Grid>
                                <Grid item size={{ xs: 'auto' }}>
                                    <FormControlLabel
                                        control={<Checkbox checked={form.isPopular} onChange={handleCheckboxChange} name="isPopular" />}
                                        label="Popular"
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Additional Details */}
                        <Box sx={{ bgcolor: '#fff', p: 3, borderRadius: 2, mb: 3, border: '1px solid lightgrey' }}>
                            <Typography variant="h6" gutterBottom>Additional Details</Typography>
                            <Grid container spacing={2}>
                                <Grid item size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Edition"
                                        name="edition"
                                        value={form.edition}
                                        error={!!formErrors.edition}
                                        helperText={formErrors.edition}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Code"
                                        name="code"
                                        value={form.code}
                                        error={!!formErrors.code}
                                        helperText={formErrors.code}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Language"
                                        name="language"
                                        value={form.language}
                                        error={!!formErrors.language}
                                        helperText={formErrors.language}
                                        onChange={handleChange}
                                    >
                                        {languages?.map(lang => (
                                            <MenuItem key={lang.id} value={lang.name}>{lang.name}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Format"
                                        name="format"
                                        value={form.format}
                                        error={!!formErrors.format}
                                        helperText={formErrors.format}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="ISBN *"
                                        name="ISBN"
                                        value={form.ISBN}
                                        onChange={handleChange}
                                        error={!!formErrors.ISBN}
                                        helperText={formErrors.ISBN}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Publishing Year"
                                        name="publishingYear"
                                        value={form.publishingYear}
                                        error={!!formErrors.publishingYear}
                                        helperText={formErrors.publishingYear}
                                        onChange={handleChange}
                                    />

                                </Grid>
                                <Grid item size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Preview Video URL"
                                        name="previewVideoUrl"
                                        value={form.previewVideoUrl}
                                        onChange={handleChange}
                                        placeholder="https://www.youtube.com/watch?v=example"
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12 }}>
                                    <HtmlEditor
                                        value={form.aboutTheBook}
                                        onChange={(value) => setForm({ ...form, aboutTheBook: value })}
                                        placeholder="Enter book details here..."
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Upload Media */}
                        <Box sx={{ bgcolor: '#fff', p: 3, borderRadius: 2, mb: 3, border: '1px solid lightgrey' }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                component="label"
                                disabled={bookImages.length >= 4}
                            >
                                Upload Book Images (max 4)
                                <input
                                    hidden
                                    type="file"
                                    name="bookImage"
                                    onChange={handleFileChange}
                                    accept="image/*"
                                    multiple
                                />
                            </Button>
                            {/* {bookImages.map((item, idx) => (
                                <Box key={idx}>
                                    <img src={item.preview} />
                                    <IconButton onClick={() => handleRemoveBookImage(idx)}>X</IconButton>
                                </Box>
                            ))} */}
                            <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
                                {bookImages.map((src, idx) => (
                                    <Box
                                        key={idx}
                                        sx={{
                                            position: 'relative',
                                            width: 100,
                                            height: 120,
                                            mr: 1,
                                            mb: 1,
                                            display: 'inline-block'
                                        }}
                                    >
                                        <img
                                            src={src.preview}
                                            alt={`Book preview ${idx + 1}`}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: 4
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: 2,
                                                right: 2,
                                                bgcolor: 'rgba(255,255,255,0.7)',
                                                zIndex: 2
                                            }}
                                            onClick={() => handleRemoveBookImage(idx)}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        </Box>


                        {/* pdf upload */}

                        <Box sx={{ bgcolor: '#fff', p: 3, borderRadius: 2, mb: 3, border: '1px solid lightgrey' }}>
                            <Typography variant="h6" gutterBottom>Upload Preview PDF</Typography>
                            <Button fullWidth variant="outlined" component="label">
                                Upload PDF (only one)
                                <input
                                    hidden
                                    type="file"
                                    accept="application/pdf"
                                    onChange={handlePdfUpload}
                                />
                            </Button>

                            {previewPdfUrl && (
                                <Box mt={2}>
                                    <Typography>Preview:</Typography>
                                    <a href={previewPdfUrl} target="_blank" rel="noopener noreferrer">
                                        Open PDF Preview
                                    </a>

                                    {/* Optional: embed inline preview */}
                                    <iframe
                                        src={previewPdfUrl}
                                        width="100%"
                                        height="300px"
                                        style={{ border: '1px solid #ccc', borderRadius: '4px', marginTop: 8 }}
                                        title="PDF Preview"
                                    />
                                </Box>
                            )}

                        </Box>

                        {/* Action Buttons */}
                        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={() => navigate('/display-books')}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                            >
                                {loading ? (editingBook ? 'Updating...' : 'Creating...') : 'Save Book'}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default AddEditBooks;
